import {
	CANONICAL_LICENSE_STATUSES,
	CANONICAL_STATUS_META,
	statusCanOrder,
	type CanonicalLicenseStatus,
} from '../constants/license-status-canonical';

/**
 * The expired-term label on a registry row (PD-17099, decision #1, Chris
 * 2026-10-14).
 *
 * `lm_registry.license_status_normalized` reads 'Expired' once a license's own
 * expiration has passed, while `lm_registry.license_status` keeps the board's
 * raw word, which can still read in force ('Active', 'Clear', 'C'). Before the
 * decision those rows read 'Active' with a past expiration. The licensing
 * pipeline writes the rule (scripts/license-management/lib/expired-term-status.mjs);
 * this module is how crm-api tells that label apart from an 'Expired' the
 * board itself published.
 *
 * WHY READERS NEED IT. A few crm-api rules used the label 'Active' to mean
 * "the board still says this license is in force" and then judged the date on
 * its own, e.g. the PD-14597 order fallback to the tenant's renewed date and
 * the PD-16387 later-expiration guard. Those rules must treat a term-derived
 * 'Expired' the way they treated 'Active' over a past date, and must keep
 * treating a board's own 'Expired' as expired.
 */

/** The canonical the expired-term rule writes. */
export const EXPIRED_TERM_STATUS: CanonicalLicenseStatus = 'Expired';

/**
 * Canonicals the expired-term rule may replace: in force and not adverse.
 * Derived from CANONICAL_STATUS_META the same way the pipeline derives
 * EXPIRED_TERM_REPLACEABLE, so the two cannot disagree. Today: ['Active'].
 */
export const EXPIRED_TERM_REPLACEABLE_STATUSES: ReadonlyArray<CanonicalLicenseStatus> =
	CANONICAL_LICENSE_STATUSES.filter(
		(s) => CANONICAL_STATUS_META[s].canOrder && !CANONICAL_STATUS_META[s].isAdverse,
	);

/** The registry columns the predicate reads. */
export interface RegistryExpiredTermInputModel {
	/** `lm_registry.license_status_normalized`. */
	licenseStatus: string | null;
	/**
	 * The canonical of the board's own word (`lm_registry.license_status`)
	 * through `lm_status_normalization`, state entry first, then '*'. Loaders
	 * project it with `registryBoardWordStatusSql` (crm-api). Absent when the
	 * loader did not project it, which reads as "not term-derived".
	 */
	boardWordStatus?: string | null;
	expirationDate: string | Date | null;
}

function toCalendarDate(value: string | Date | null): string | null {
	if (value == null) {
		return null;
	}
	if (value instanceof Date) {
		return Number.isNaN(value.getTime())
			? null
			: value.toISOString().slice(0, 10);
	}
	const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
	return match?.[1] ?? null;
}

/**
 * True when the row's 'Expired' label is the expired-term rule's rather than
 * the board's: the label reads Expired, the board's word reads in force, and
 * the row's own expiration has passed (a license is valid THROUGH its
 * expiration date, so the expiration day itself never counts). A board that
 * published 'Expired', 'Lapsed' or 'Not Renewed' is never term-derived, and
 * neither is a row whose word has no dictionary entry.
 */
export function isRegistryStatusExpiredByTerm(
	row: RegistryExpiredTermInputModel | null | undefined,
	today: Date = new Date(),
): boolean {
	if (row == null || row.licenseStatus !== EXPIRED_TERM_STATUS) {
		return false;
	}
	const word = row.boardWordStatus;
	if (
		word == null ||
		!EXPIRED_TERM_REPLACEABLE_STATUSES.some((status) => status === word)
	) {
		return false;
	}
	const expiration = toCalendarDate(row.expirationDate);
	// mysql2 reads a '0000-00-00' DATE as 1899-11-30: an absence, not a term.
	if (expiration == null || expiration < '1900-01-01') {
		return false;
	}
	return expiration < today.toISOString().slice(0, 10);
}

/**
 * Order standing for a license whose effective status is `status` (PD-14745),
 * reading a term-derived 'Expired' as the in-force word it replaced. The order
 * check judges the date separately (`isExpired`, through the PD-14597
 * fallback), so a lapsed term still fails there exactly as it did when the
 * label read 'Active'; failing it again here would block an order the tenant's
 * renewed date allows. A board's own 'Expired' stays non-orderable.
 */
export function orderStandingCanOrder(
	status: string | null | undefined,
	registry: RegistryExpiredTermInputModel | null | undefined,
	today: Date = new Date(),
): boolean {
	return statusCanOrder(status) || isRegistryStatusExpiredByTerm(registry, today);
}

/** The review screen's note for a not-in-force registration a person may link. */
export interface NotInForceLinkNoteModel {
	badge: string;
	text: string;
}

/**
 * What the match review tells a reviewer before they link a customer's own
 * not-in-force license on an OPEN account (PD-17154), with nothing in force
 * holding the slot (PD-17231). Chris's rules hold for every status: the link is
 * kept so the renewal can be found, it shows on Dead License, and it never
 * counts as coverage.
 *
 * PD-17099 (decision #1): for the expired-term 'Expired' (`expiredByTerm`,
 * the registry detail's `status_expired_by_term`) the regulator has not said
 * Expired; the board's word still reads in force and the license's own date
 * has passed. Saying "Expired at the regulator" would misstate it, so the note
 * says the term ended. The rules it states are the same.
 */
export function notInForceLinkNote(
	status: string,
	expiredByTerm: boolean,
	expirationDate: string | null = null,
): NotInForceLinkNoteModel {
	const outcome =
		"If it is this customer's own license, you can confirm it: the match is kept so its renewal can be found. It will show on Dead License, not as coverage.";
	if (expiredByTerm) {
		const date = toCalendarDate(expirationDate);
		return {
			badge: 'Expired: term ended',
			text: `This license's expiration date${date != null ? ` (${date})` : ''} has passed, although the board still lists it in force. ${outcome}`,
		};
	}
	return {
		badge: `${status} at the regulator`,
		text: `This license is ${status} at the regulator. ${outcome}`,
	};
}
